import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface Recipient {
  id: string;
  email: string;
  full_name: string | null;
  daily_reports: boolean;
  weekly_reports: boolean;
  monthly_reports: boolean;
  active: boolean;
  added_at: string;
}

export default function ReportRecipientsPage() {
  const [list, setList] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const refresh = useCallback(async () => {
    try {
      const r = await apiClient.get<Recipient[]>('/api/v1/reports-config/recipients');
      setList(r.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function add() {
    setAdding(true);
    try {
      await apiClient.post('/api/v1/reports-config/recipients', { email: email.trim(), fullName: name.trim() || undefined });
      setEmail(''); setName('');
      await refresh();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    } finally {
      setAdding(false);
    }
  }

  async function toggle(id: string, field: 'daily_reports' | 'weekly_reports' | 'monthly_reports' | 'active', value: boolean) {
    const map: Record<string, string> = { daily_reports: 'dailyReports', weekly_reports: 'weeklyReports', monthly_reports: 'monthlyReports', active: 'active' };
    try {
      await apiClient.patch('/api/v1/reports-config/recipients/' + id, { [map[field]]: value });
      await refresh();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    }
  }

  async function remove(id: string) {
    if (!window.confirm('Deactivate this recipient?')) return;
    try {
      await apiClient.delete('/api/v1/reports-config/recipients/' + id);
      await refresh();
    } catch (err) { window.alert(extractErrorMessage(err)); }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Mail className="h-4 w-4" /> Report recipients</div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        <div className="rounded-lg border border-border p-3 flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <Label htmlFor="em">Email</Label>
            <Input id="em" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" />
          </div>
          <div className="flex-1 space-y-1">
            <Label htmlFor="nm">Name</Label>
            <Input id="nm" value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" />
          </div>
          <Button onClick={add} disabled={adding || !email} className="gap-2">{adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add</Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>
        ) : list.length === 0 ? (
          <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No recipients yet.</div>
        ) : (
          <div className="space-y-2">
            {list.map((r) => (
              <div key={r.id} className={'rounded-lg border p-3 ' + (r.active ? 'border-border' : 'border-border opacity-50')}>
                <div className="flex items-baseline justify-between gap-2">
                  <div>
                    <div className="text-sm font-medium">{r.email}</div>
                    {r.full_name && <div className="text-xs text-muted-foreground">{r.full_name}</div>}
                  </div>
                  <button onClick={() => remove(r.id)} className="text-xs text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Deactivate</button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2 text-xs">
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={r.daily_reports} onChange={(e) => toggle(r.id, 'daily_reports', e.target.checked)} disabled={!r.active} /> Daily
                  </label>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={r.weekly_reports} onChange={(e) => toggle(r.id, 'weekly_reports', e.target.checked)} disabled={!r.active} /> Weekly
                  </label>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={r.monthly_reports} onChange={(e) => toggle(r.id, 'monthly_reports', e.target.checked)} disabled={!r.active} /> Monthly
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-800 dark:text-amber-400 p-3 text-xs">
          ⚠ SMTP not configured. Until you set <code>SMTP_HOST</code>, <code>SMTP_USER</code>, <code>SMTP_PASS</code> in
          <code> /opt/jasrhub/config/.env</code> the worker logs &quot;would have sent N emails&quot; instead of actually sending.
        </div>
      </main>
    </div>
  );
}
